import { mealCaloriePercent, mealOrder, parseScaledRecipeId, buildScaledRecipeId, recipeHasDiscreteSnap, recipeHasContinuousAbsorber, type MealKey, type Recipe } from "@/data/recipes";
import { getScaleLimits } from "@/lib/macroSolver";
import { analyzeMealOptionCandidates, pickClosestMealOptionState, type MealOptionState } from "@/lib/dietOptionRebalanceCore";

interface RebalanceMealOptionInput {
  optionIds: string[];
  targetCalories: number;
  resolveRecipe: (id: string) => Recipe | undefined;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function getNormalizedMealBudgets(
  targetCalories: number,
  activeMeals: MealKey[],
): Record<MealKey, number> {
  const roundedTarget = Math.max(0, Math.round(targetCalories));
  const budgets = {} as Record<MealKey, number>;

  if (activeMeals.length === 0) return budgets;

  const totalPct = activeMeals.reduce((sum, mealKey) => sum + mealCaloriePercent[mealKey], 0);
  const rawBudgets = activeMeals.map((mealKey) => {
    const raw = roundedTarget * (mealCaloriePercent[mealKey] / totalPct);
    return {
      mealKey,
      base: Math.floor(raw),
      fraction: raw - Math.floor(raw),
    };
  });

  let remaining = roundedTarget - rawBudgets.reduce((sum, item) => sum + item.base, 0);

  rawBudgets
    .sort((a, b) => b.fraction - a.fraction)
    .forEach((item) => {
      budgets[item.mealKey] = item.base;
    });

  for (const item of rawBudgets) {
    if (remaining <= 0) break;
    budgets[item.mealKey] += 1;
    remaining -= 1;
  }

  return budgets;
}

export function rebalanceMealOptionToTarget({
  optionIds,
  targetCalories,
  resolveRecipe,
}: RebalanceMealOptionInput): string[] {
  if (optionIds.length === 0) return optionIds;

  const analysis = analyzeMealOptionCandidates({ optionIds, resolveRecipe });
  const bestState = pickClosestMealOptionState(analysis.states, targetCalories);

  if (!bestState) return optionIds;
  return analysis.buildOptionIds(bestState.picks);
}

/* ──────────────────────────────────────────────────────────
 *  Exact meal closure helper
 *  Goal: get a single meal option to land within [99%, 101%]
 *  of its budget (or as close as structurally possible).
 *
 *  Strategy:
 *   1. Solve with current items (rebalanceMealOptionToTarget).
 *   2. If overshoots ceiling (>110%), nothing else to do — caller
 *      should clamp gramagens. We still return the best fit.
 *   3. If undershoots (< target*0.99) and we have candidates from
 *      `availablePreferences` not yet in the option, try adding ONE
 *      candidate at a time and re-solving. Pick the addition that
 *      brings the resulting total closest to target without exceeding
 *      `target * maxOverRatio` (default 1.05). Repeat up to
 *      `maxAdditions` times.
 * ────────────────────────────────────────────────────────── */

interface CloseMealOptionInput {
  optionIds: string[];
  targetCalories: number;
  resolveRecipe: (id: string) => Recipe | undefined;
  /** Recipe IDs the user accepts for this meal (preferences pool). */
  availablePreferences?: string[];
  /** Max additions allowed when there is structural undershoot. */
  maxAdditions?: number;
  /** Acceptable upper bound (target * maxOverRatio) for any addition. */
  maxOverRatio?: number;
  /** Acceptable lower bound (target * acceptRatio) → stop trying. */
  acceptRatio?: number;
}

function getOptionDisplayedCalories(
  optionIds: string[],
  resolveRecipe: (id: string) => Recipe | undefined,
): number {
  return optionIds.reduce((sum, id) => {
    const recipe = resolveRecipe(id);
    if (!recipe || recipe.category === "salad") return sum;
    return sum + recipe.cal;
  }, 0);
}

/**
 * Pick the state closest to target but preferring states within the
 * [floor, ceiling] acceptance band when one exists.
 * This avoids the solver returning a far-below-floor state simply because
 * it happens to be the absolute closest candidate.
 */
function pickBandedState(
  states: MealOptionState[],
  target: number,
  floor: number,
  ceiling: number,
): MealOptionState | null {
  if (states.length === 0) return null;
  const inBand = states.filter((s) => s.totalCalories >= floor && s.totalCalories <= ceiling);
  if (inBand.length > 0) {
    // Among in-band states pick the one closest to target (minimum movement as tiebreak).
    let best = inBand[0];
    let bestDiff = Math.abs(best.totalCalories - target);
    for (const s of inBand.slice(1)) {
      const d = Math.abs(s.totalCalories - target);
      if (d < bestDiff || (d === bestDiff && s.movement < best.movement)) {
        best = s;
        bestDiff = d;
      }
    }
    return best;
  }
  // No state in band → prefer the closest UNDERSHOOT (≤ ceiling) over any
  // overshoot. Undershoot is absorbed by the cross-meal rebalancer; overshoot
  // visibly explodes the meal budget (e.g. panqueca scaling 230→544 kcal due
  // to discrete egg/banana snapping). Only fall back to overshoot if there is
  // literally no state ≤ ceiling (degenerate case where minimums already
  // exceed the limit).
  const undershoot = states.filter((s) => s.totalCalories <= ceiling);
  if (undershoot.length > 0) {
    let best = undershoot[0];
    let bestDiff = Math.abs(best.totalCalories - target);
    for (const s of undershoot.slice(1)) {
      const d = Math.abs(s.totalCalories - target);
      if (d < bestDiff || (d === bestDiff && s.movement < best.movement)) {
        best = s;
        bestDiff = d;
      }
    }
    return best;
  }
  return pickClosestMealOptionState(states, target);
}

/* ──────────────────────────────────────────────────────────
 *  Post-snap compensation
 *
 *  After the DP solver picks the best discrete state, the option may still
 *  fall outside the [acceptFloor, acceptCeiling] window because the DP only
 *  controls one scale per recipe and discrete ingredients (eggs, bread slices,
 *  whole fruits) snap to the nearest unit, leaving 10–30 kcal gaps.
 *
 *  Strategy:
 *   1. Classify each recipe in the option as continuous (no snap) or discrete.
 *   2. If under floor: bump continuous recipes proportionally to absorb the
 *      missing kcal (priority: heaviest kcal first). If still under and no
 *      continuous capacity, try +1 step on the lightest discrete item — but
 *      only if it does not exceed the ceiling.
 *   3. If over ceiling: compress continuous recipes proportionally.
 *   4. Never add or remove items.
 * ────────────────────────────────────────────────────────── */

interface CompensationItem {
  index: number;
  baseId: string;
  isFree: boolean;
  isSalad: boolean;
  isContinuous: boolean;
  currentScale: number;
  minScale: number;
  maxScale: number;
  currentCal: number;
  /** kcal change per +0.05 scale step at currentScale (signed positive) */
  kcalPer05: number;
}

function buildCompensationItems(
  optionIds: string[],
  resolveRecipe: (id: string) => Recipe | undefined,
): CompensationItem[] {
  const items: CompensationItem[] = [];
  for (let i = 0; i < optionIds.length; i += 1) {
    const id = optionIds[i];
    const isFree = id.endsWith(":free");
    const recipe = resolveRecipe(id);
    if (!recipe) continue;
    const isSalad = recipe.category === "salad";
    if (isFree || isSalad) {
      items.push({
        index: i,
        baseId: parseScaledRecipeId(id.replace(/:free$/, "")).baseId,
        isFree,
        isSalad,
        isContinuous: false,
        currentScale: 1,
        minScale: 1,
        maxScale: 1,
        currentCal: isSalad ? 0 : recipe.cal,
        kcalPer05: 0,
      });
      continue;
    }
    const { baseId, scale } = parseScaledRecipeId(id);
    const baseRecipe = resolveRecipe(baseId);
    if (!baseRecipe) continue;
    const { minScale, maxScale } = getScaleLimits(baseRecipe);
    // Treat as continuous if either the recipe is fully smooth (no discrete snap)
    // OR it contains at least one continuous absorber (e.g. leite/aveia in a
    // vitamina with banana). Mixed recipes can still grow proportionally because
    // the discrete ingredient is held back by its own snap inside applyScaleToRecipe,
    // so additional scale flows to the absorbers.
    const isContinuous = !recipeHasDiscreteSnap(baseRecipe) || recipeHasContinuousAbsorber(baseRecipe);
    // Sample kcal-per-step empirically (handles snap and floors).
    const probeScale = Math.min(maxScale, scale + 0.05);
    const probed = resolveRecipe(buildScaledRecipeId(baseId, probeScale));
    const kcalPer05 = probed ? Math.max(0, probed.cal - recipe.cal) : 0;
    items.push({
      index: i,
      baseId,
      isFree: false,
      isSalad: false,
      isContinuous,
      currentScale: scale,
      minScale,
      maxScale,
      currentCal: recipe.cal,
      kcalPer05,
    });
  }
  return items;
}

function compensateOptionPostSnap({
  optionIds,
  targetCalories,
  acceptFloor,
  acceptCeiling,
  resolveRecipe,
}: {
  optionIds: string[];
  targetCalories: number;
  acceptFloor: number;
  acceptCeiling: number;
  resolveRecipe: (id: string) => Recipe | undefined;
}): string[] {
  if (optionIds.length === 0) return optionIds;
  let currentCal = getOptionDisplayedCalories(optionIds, resolveRecipe);
  if (currentCal >= acceptFloor && currentCal <= acceptCeiling) return optionIds;

  const result = [...optionIds];
  const items = buildCompensationItems(result, resolveRecipe);
  const adjustable = items.filter((it) => !it.isFree && !it.isSalad);
  if (adjustable.length === 0) return result;

  const continuous = adjustable.filter((it) => it.isContinuous);

  // Helper: re-apply a new scale to an item and refresh its currentCal/currentScale.
  const applyScale = (item: CompensationItem, newScale: number) => {
    const clamped = Math.max(item.minScale, Math.min(item.maxScale, newScale));
    if (Math.abs(clamped - item.currentScale) < 1e-4) return;
    result[item.index] = buildScaledRecipeId(item.baseId, clamped);
    const refreshed = resolveRecipe(result[item.index]);
    if (refreshed) {
      item.currentScale = clamped;
      item.currentCal = refreshed.cal;
    }
  };

  // ── UNDER FLOOR ── bump continuous absorbers, then try +1 discrete unit.
  if (currentCal < acceptFloor) {
    if (continuous.length > 0) {
      // Iterate up to 6 passes — each pass redistributes the residual delta
      // across continuous items proportional to current kcal weight.
      for (let pass = 0; pass < 6; pass += 1) {
        currentCal = getOptionDisplayedCalories(result, resolveRecipe);
        const delta = targetCalories - currentCal;
        if (delta <= 0 || currentCal >= acceptFloor) break;

        const totalContinuousCal = continuous.reduce((s, it) => s + it.currentCal, 0);
        if (totalContinuousCal <= 0) break;

        // Sort heaviest first so the dominant absorber moves first.
        const ordered = [...continuous].sort((a, b) => b.currentCal - a.currentCal);
        let absorbed = 0;
        for (const item of ordered) {
          if (absorbed >= delta) break;
          const headroomScale = item.maxScale - item.currentScale;
          if (headroomScale <= 1e-4) continue;
          const share = (item.currentCal / totalContinuousCal) * delta;
          // kcal/scale ratio at current point (avoid /0)
          const kcalPerScale = item.currentCal > 0 && item.currentScale > 0
            ? item.currentCal / item.currentScale
            : 0;
          if (kcalPerScale <= 0) continue;
          const desiredScaleDelta = share / kcalPerScale;
          const appliedScaleDelta = Math.min(desiredScaleDelta, headroomScale);
          const newScale = item.currentScale + appliedScaleDelta;
          const prevCal = item.currentCal;
          applyScale(item, newScale);
          absorbed += item.currentCal - prevCal;
        }
        if (absorbed <= 0.5) break; // no more capacity
      }
    }

    // If still under floor, try incrementing the lightest discrete recipe by
    // one structural step (≈ +1 unit / +1 slice). Keep only if it stays under
    // the ceiling AND lands closer to target than the current state.
    currentCal = getOptionDisplayedCalories(result, resolveRecipe);
    if (currentCal < acceptFloor) {
      const discrete = adjustable
        .filter((it) => !it.isContinuous && it.kcalPer05 > 0)
        .sort((a, b) => a.kcalPer05 - b.kcalPer05);

      for (const item of discrete) {
        if (item.currentScale >= item.maxScale - 1e-4) continue;
        // Try increments of +0.1 scale until we either cross the floor or the ceiling.
        let trialScale = item.currentScale;
        let bestScale = item.currentScale;
        let bestDiff = Math.abs(currentCal - targetCalories);
        let bestCal = currentCal;
        for (let step = 0; step < 20; step += 1) {
          trialScale += 0.1;
          if (trialScale > item.maxScale) break;
          const probed = resolveRecipe(buildScaledRecipeId(item.baseId, trialScale));
          if (!probed) continue;
          const newOptionCal = currentCal - item.currentCal + probed.cal;
          if (newOptionCal > acceptCeiling) break;
          const diff = Math.abs(newOptionCal - targetCalories);
          if (diff < bestDiff) {
            bestDiff = diff;
            bestScale = trialScale;
            bestCal = newOptionCal;
          }
          if (newOptionCal >= acceptFloor) break;
        }
        if (bestScale > item.currentScale + 1e-4) {
          applyScale(item, bestScale);
          currentCal = bestCal;
          if (currentCal >= acceptFloor) break;
        }
      }
    }
    return result;
  }

  // ── OVER CEILING ── compress continuous absorbers proportionally.
  if (currentCal > acceptCeiling) {
    if (continuous.length === 0) return result;
    for (let pass = 0; pass < 6; pass += 1) {
      currentCal = getOptionDisplayedCalories(result, resolveRecipe);
      const delta = currentCal - targetCalories; // positive overshoot
      if (delta <= 0 || currentCal <= acceptCeiling) break;

      const totalContinuousCal = continuous.reduce((s, it) => s + it.currentCal, 0);
      if (totalContinuousCal <= 0) break;

      const ordered = [...continuous].sort((a, b) => b.currentCal - a.currentCal);
      let released = 0;
      for (const item of ordered) {
        if (released >= delta) break;
        const headroomScale = item.currentScale - item.minScale;
        if (headroomScale <= 1e-4) continue;
        const share = (item.currentCal / totalContinuousCal) * delta;
        const kcalPerScale = item.currentCal > 0 && item.currentScale > 0
          ? item.currentCal / item.currentScale
          : 0;
        if (kcalPerScale <= 0) continue;
        const desiredScaleDelta = share / kcalPerScale;
        const appliedScaleDelta = Math.min(desiredScaleDelta, headroomScale);
        const newScale = item.currentScale - appliedScaleDelta;
        const prevCal = item.currentCal;
        applyScale(item, newScale);
        released += prevCal - item.currentCal;
      }
      if (released <= 0.5) break;
    }
  }

  return result;
}

export function closeMealOptionToBudget({
  optionIds,
  targetCalories,
  resolveRecipe,
  availablePreferences = [],
  maxAdditions = 2,
  maxOverRatio = 1.05,
  acceptRatio = 0.99,
}: CloseMealOptionInput): string[] {
  if (optionIds.length === 0 || targetCalories <= 0) return optionIds;

  const acceptFloor = targetCalories * acceptRatio;
  const acceptCeiling = targetCalories * maxOverRatio;

  // Step 1: solve with current items using banded preference.
  const analysis = analyzeMealOptionCandidates({ optionIds, resolveRecipe });
  const bandedState = pickBandedState(analysis.states, targetCalories, acceptFloor, acceptCeiling);
  let current = bandedState ? analysis.buildOptionIds(bandedState.picks) : optionIds;
  let currentCal = getOptionDisplayedCalories(current, resolveRecipe);

  // Step 1.5 (NEW): post-snap compensation — adjust continuous absorbers
  // (whey, milk, cheese, oil, yogurt, etc.) to close residual gaps left by
  // discrete snapping (eggs, bread slices, whole fruits).
  if (currentCal < acceptFloor || currentCal > acceptCeiling) {
    current = compensateOptionPostSnap({
      optionIds: current,
      targetCalories,
      acceptFloor,
      acceptCeiling,
      resolveRecipe,
    });
    currentCal = getOptionDisplayedCalories(current, resolveRecipe);
  }

  if (currentCal >= acceptFloor && currentCal <= acceptCeiling) return current;
  // If we are over the ceiling, current state is already best fit → caller clamps.
  if (currentCal > acceptCeiling) return current;
  if (availablePreferences.length === 0) return current;

  // Step 2: try adding candidate items (up to maxAdditions).
  for (let attempt = 0; attempt < maxAdditions; attempt += 1) {
    const presentBaseIds = new Set(
      current.map((id) => parseScaledRecipeId(id.replace(/:free$/, "")).baseId),
    );

    let bestCandidateOption: string[] | null = null;
    let bestCandidateCal = currentCal;
    let bestDiff = Math.abs(currentCal - targetCalories);

    for (const candidateId of availablePreferences) {
      const { baseId } = parseScaledRecipeId(candidateId.replace(/:free$/, ""));
      if (presentBaseIds.has(baseId)) continue;
      const candRecipe = resolveRecipe(candidateId);
      if (!candRecipe) continue;
      // Skip salads & already-free beverages — they don't contribute to displayed kcal.
      if (candRecipe.category === "salad") continue;
      if (candidateId.endsWith(":free")) continue;

      const trialIds = [...current, candidateId];
      const trialAnalysis = analyzeMealOptionCandidates({ optionIds: trialIds, resolveRecipe });
      const trialState = pickBandedState(trialAnalysis.states, targetCalories, acceptFloor, acceptCeiling);
      if (!trialState) continue;
      const solved = trialAnalysis.buildOptionIds(trialState.picks);
      const solvedCal = getOptionDisplayedCalories(solved, resolveRecipe);
      // Reject additions that overshoot the acceptable ceiling.
      if (solvedCal > acceptCeiling) continue;
      const diff = Math.abs(solvedCal - targetCalories);
      if (diff < bestDiff) {
        bestDiff = diff;
        bestCandidateCal = solvedCal;
        bestCandidateOption = solved;
      }
    }

    if (!bestCandidateOption) break;
    current = bestCandidateOption;
    currentCal = bestCandidateCal;
    if (currentCal >= acceptFloor) break;
  }

  // Final pass: post-snap compensation on whatever we ended up with.
  if (currentCal < acceptFloor || currentCal > acceptCeiling) {
    current = compensateOptionPostSnap({
      optionIds: current,
      targetCalories,
      acceptFloor,
      acceptCeiling,
      resolveRecipe,
    });
  }

  return current;
}


/* ──────────────────────────────────────────────────────────
 *  Cross-meal rebalancing
 *  After modifying one meal option, redistribute the calorie
 *  surplus/deficit across other meals so the daily total stays
 *  on target.
 * ────────────────────────────────────────────────────────── */

interface CrossMealRebalanceInput {
  /** Full meals object */
  meals: Record<MealKey, string[][]>;
  /** The meal that was just modified (already rebalanced individually) */
  modifiedMealKey: MealKey;
  /** Option index within the modified meal that was already rebalanced */
  modifiedOptIdx: number;
  /** Total daily calorie target */
  dailyCalorieTarget: number;
  /** Resolve any recipe ID to a Recipe */
  resolveRecipe: (id: string) => Recipe | undefined;
  /** Active option per meal in the current UI combination */
  selectedOptionIndices?: Partial<Record<MealKey, number>>;
  /** When true, lock the modified meal's calories and only rebalance other meals */
  lockModifiedMeal?: boolean;
}

function getOptionCalories(
  optionIds: string[],
  resolveRecipe: (id: string) => Recipe | undefined,
): number {
  return optionIds.reduce((sum, id) => {
    const recipe = resolveRecipe(id);
    if (!recipe || recipe.category === "salad") return sum;
    return sum + recipe.cal;
  }, 0);
}

function resolveSelectedOptionIndex(options: string[][], preferredIndex?: number): number {
  if (typeof preferredIndex === "number" && preferredIndex >= 0 && options[preferredIndex]?.length) {
    return preferredIndex;
  }

  return options.findIndex((opt) => opt.length > 0);
}

function shouldReplaceDailyState(
  candidateCalories: number,
  candidateMovement: number,
  bestCalories: number,
  bestMovement: number,
  targetCalories: number,
): boolean {
  const candidateDiff = Math.abs(candidateCalories - targetCalories);
  const bestDiff = Math.abs(bestCalories - targetCalories);

  if (candidateDiff !== bestDiff) return candidateDiff < bestDiff;

  const candidateOverTarget = candidateCalories > targetCalories;
  const bestOverTarget = bestCalories > targetCalories;
  if (candidateOverTarget !== bestOverTarget) return !candidateOverTarget;

  if (candidateMovement !== bestMovement) return candidateMovement < bestMovement;
  return candidateCalories < bestCalories;
}

/**
 * Rebalances ALL meals in the diet after a single meal option was modified.
 *
 * 1. Computes the budget for the modified meal based on its rebalanced option.
 * 2. Rebalances all OTHER options of the modified meal to the same budget.
 * 3. Distributes the remaining calorie budget proportionally to other meals.
 * 4. Rebalances ALL options of each other meal to their adjusted budget.
 *
 * Returns a new meals object (does not mutate the input).
 */
export function rebalanceDietAcrossMeals({
  meals,
  modifiedMealKey,
  modifiedOptIdx,
  dailyCalorieTarget,
  resolveRecipe,
  selectedOptionIndices,
  lockModifiedMeal = false,
}: CrossMealRebalanceInput): Record<MealKey, string[][]> {
  const roundedTarget = Math.max(0, Math.round(dailyCalorieTarget));
  const result = {} as Record<MealKey, string[][]>;

  for (const key of mealOrder) {
    result[key] = (meals[key] || []).map((opt) => [...opt]);
  }

  const activeMeals = mealOrder.filter((key) => {
    const options = result[key] || [];
    return options.some((opt) => opt.length > 0);
  });

  // Build meal analysis for all active meals
  // Only rebalance the modified option index across all meals
  const targetOptIdx = modifiedOptIdx;

  const allSelectedMeals = activeMeals
    .map((mealKey) => {
      const options = result[mealKey] || [];
      const selectedOptIdx = targetOptIdx;

      if (selectedOptIdx < 0 || !options[selectedOptIdx]?.length) return null;

      return {
        mealKey,
        selectedOptIdx,
        analysis: analyzeMealOptionCandidates({ optionIds: options[selectedOptIdx], resolveRecipe }),
      };
    })
    .filter(
      (
        meal,
      ): meal is {
        mealKey: MealKey;
        selectedOptIdx: number;
        analysis: ReturnType<typeof analyzeMealOptionCandidates>;
      } => Boolean(meal),
    );

  if (allSelectedMeals.length === 0) return result;

  // When lockModifiedMeal is true, the modified meal keeps its current calories
  // and only the other meals are adjusted via the DP solver.
  let lockedMealCalories = 0;
  let lockedMealEntry: (typeof allSelectedMeals)[number] | null = null;
  let solverMeals = allSelectedMeals;

  if (lockModifiedMeal) {
    lockedMealEntry = allSelectedMeals.find((m) => m.mealKey === modifiedMealKey) ?? null;
    if (lockedMealEntry) {
      // Use the current option as-is (already locally rebalanced by the caller)
      lockedMealCalories = getOptionCalories(
        result[lockedMealEntry.mealKey][lockedMealEntry.selectedOptIdx],
        resolveRecipe,
      );
      solverMeals = allSelectedMeals.filter((m) => m.mealKey !== modifiedMealKey);
    }
  }

  const solverTarget = Math.max(0, roundedTarget - lockedMealCalories);

  if (solverMeals.length === 0) {
    // Only the locked meal exists — rebalance its other options to match
    if (lockedMealEntry) {
      applyMealResults(result, [lockedMealEntry], [0], resolveRecipe);
    }
    return result;
  }

  // DP solver across solver meals
  type DpState = { movement: number; picks: number[] };
  let states = new Map<number, DpState>([[0, { movement: 0, picks: [] }]]);

  for (const meal of solverMeals) {
    const nextStates = new Map<number, DpState>();

    for (const [currentCalories, state] of states.entries()) {
      for (let candidateIndex = 0; candidateIndex < meal.analysis.states.length; candidateIndex += 1) {
        const candidate = meal.analysis.states[candidateIndex];
        const totalCalories = currentCalories + candidate.totalCalories;
        const movement = state.movement + candidate.movement;
        const existing = nextStates.get(totalCalories);

        if (!existing || movement < existing.movement) {
          nextStates.set(totalCalories, {
            movement,
            picks: [...state.picks, candidateIndex],
          });
        }
      }
    }

    states = nextStates;
  }

  let bestTotalCalories = 0;
  let bestState: DpState | null = null;

  for (const [totalCalories, state] of states.entries()) {
    if (
      !bestState ||
      shouldReplaceDailyState(totalCalories, state.movement, bestTotalCalories, bestState.movement, solverTarget)
    ) {
      bestTotalCalories = totalCalories;
      bestState = state;
    }
  }

  if (!bestState) return result;

  // Apply solver results to non-locked meals
  applyMealResults(result, solverMeals, bestState.picks, resolveRecipe);

  // Apply locked meal: keep selected option as-is, rebalance other options to its calories
  if (lockedMealEntry) {
    // Keep the locked meal's modified option as-is; don't touch other options
    result[lockedMealEntry.mealKey] = (result[lockedMealEntry.mealKey] || []).map((optionIds) => optionIds);
  }

  // Post-solver: when a meal was locked, clamp each non-locked meal to its
  // proportional budget ceiling (1.10x) to prevent small meals like optional
  // from inflating beyond their fair share.
  if (lockModifiedMeal) {
    const budgets = getNormalizedMealBudgets(roundedTarget, activeMeals);
    for (const meal of solverMeals) {
      const optionIds = result[meal.mealKey][meal.selectedOptIdx];
      if (!optionIds || optionIds.length === 0) continue;
      const mealBudget = budgets[meal.mealKey];
      if (!mealBudget || mealBudget <= 0) continue;
      const currentCal = getOptionCalories(optionIds, resolveRecipe);
      const ceiling = mealBudget * 1.10;
      if (currentCal > ceiling && currentCal > 0) {
        const correction = ceiling / currentCal;
        for (let i = 0; i < optionIds.length; i++) {
          const { baseId, scale } = parseScaledRecipeId(optionIds[i].replace(/:free$/, ""));
          if (optionIds[i].endsWith(":free")) continue;
          const baseRecipe = resolveRecipe(baseId);
          if (!baseRecipe || baseRecipe.category === "salad") continue;
          const { minScale } = getScaleLimits(baseRecipe);
          const newScale = Math.max(minScale, scale * correction);
          optionIds[i] = buildScaledRecipeId(baseId, newScale);
        }
      }
    }
  }

  return result;
}

function applyMealResults(
  result: Record<MealKey, string[][]>,
  meals: { mealKey: MealKey; selectedOptIdx: number; analysis: ReturnType<typeof analyzeMealOptionCandidates> }[],
  picks: number[],
  _resolveRecipe: (id: string) => Recipe | undefined,
) {
  meals.forEach((meal, mealIdx) => {
    const chosenMealState = meal.analysis.states[picks[mealIdx]];
    const selectedOptionIds = meal.analysis.buildOptionIds(chosenMealState.picks);

    // Only update the specific option that was modified; leave other options untouched
    const options = result[meal.mealKey] || [];
    if (meal.selectedOptIdx >= 0 && meal.selectedOptIdx < options.length) {
      options[meal.selectedOptIdx] = selectedOptionIds;
    }
  });
}